<template>
  <div class="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
    <!-- 顶部导航 -->
    <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <router-link to="/" class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
            </router-link>
            <div class="flex items-center gap-2">
              <span class="text-2xl">🎯</span>
              <span class="font-semibold text-purple-600">霍兰德职业兴趣测试结果</span>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="container mx-auto px-4 py-8">
      <div class="max-w-5xl mx-auto space-y-6">
        <!-- 1. Holland Code 展示 -->
        <HollandCodeDisplay 
          v-if="result"
          :hollandCode="result.hollandCode"
          :timestamp="result.timestamp"
        />

        <!-- 2. 六维度分析 -->
        <div class="bg-white rounded-2xl shadow-lg p-6">
          <SixDimensionAnalysis 
            v-if="result"
            :dimensionScores="result.dimensionScores"
            :rawScores="result.rawScores"
          />
        </div>


         <!-- 组合类型解读 -->
         <div class="bg-white rounded-2xl shadow-lg p-6" v-if="result">
          <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>💡</span>
            <span>您的职业兴趣组合解读</span>
          </h3>
          <div class="prose prose-lg max-w-none">
            <p class="text-gray-700 leading-relaxed whitespace-pre-line">{{ result.interpretation }}</p>
          </div>
        </div>

        <!-- 您的主要职业类型 -->
        <PrimaryTypeCard 
          v-if="primaryTypeInfo"
          :typeInfo="primaryTypeInfo"
        />

       

        <!-- 适合您的职业方向 -->
        <CareerDirections
          v-if="primaryTypeInfo && secondaryTypeInfo && tertiaryTypeInfo"
          :primaryType="primaryTypeInfo"
          :secondaryType="secondaryTypeInfo"
          :tertiaryType="tertiaryTypeInfo"
        />

        <!-- 职业发展建议 -->
        <CareerAdvice
          v-if="result && result.careerRecommendations"
          :recommendations="result.careerRecommendations"
          :typeColor="primaryTypeInfo?.color || '#6366f1'"
        />

        <!-- 学习与发展路径 -->
        <LearningPath
          v-if="primaryTypeInfo"
          :majors="primaryTypeInfo.majors"
          :developmentSuggestions="primaryTypeInfo.developmentSuggestions"
        />

        <!-- 典型职业详解 -->
        <TypicalCareers
          v-if="primaryTypeInfo"
          :careers="primaryTypeInfo.typicalCareers"
          :typeName="primaryTypeInfo.name"
          :typeColor="primaryTypeInfo.color"
        />

        <!-- 深度类型解读 -->
        <DeepTypeAnalysis
          v-if="primaryTypeInfo"
          :description="primaryTypeInfo.detailedDescription"
          :challenges="primaryTypeInfo.challenges"
          :typeName="primaryTypeInfo.fullName"
          :typeColor="primaryTypeInfo.color"
        />

       <!-- 职业发展路径规划 -->
       <CareerPathPlanning
          v-if="primaryTypeInfo"
          :path="primaryTypeInfo.developmentPath"
        />

        <!-- 常见问题 FAQ -->
        <HollandFAQ
          v-if="primaryTypeInfo"
          :primaryColor="primaryTypeInfo.color"
        />

        <!-- 理解您的Holland Code -->
        <HollandCodeSummary
          v-if="result"
          :hollandCode="result.hollandCode"
        />

        <!-- 操作按钮 -->
        <div class="flex justify-center gap-4 py-6">
          <!-- <button
            @click="saveResult"
            class="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold flex items-center gap-2"
          >
            <span>💾</span>
            <span>保存结果</span>
          </button> -->
          <button
            @click="retakeTest"
            class="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center gap-2"
          >
            <span>🔄</span>
            <span>重新测试</span>
          </button>
        </div>
      </div>
    </main>

    <!-- 回到顶部 -->
    <BackToTop />
  </div>
</template>

<script>
import BackToTop from '../components/BackToTop.vue'
import HollandCodeDisplay from '../components/result/HollandCodeDisplay.vue'
import SixDimensionAnalysis from '../components/result/SixDimensionAnalysis.vue'
import PrimaryTypeCard from '../components/result/PrimaryTypeCard.vue'
import CareerDirections from '../components/result/CareerDirections.vue'
import CareerAdvice from '../components/result/CareerAdvice.vue'
import LearningPath from '../components/result/LearningPath.vue'
import TypicalCareers from '../components/result/TypicalCareers.vue'
import DeepTypeAnalysis from '../components/result/DeepTypeAnalysis.vue'
import CareerPathPlanning from '../components/result/CareerPathPlanning.vue'
import HollandFAQ from '../components/result/HollandFAQ.vue'
import HollandCodeSummary from '../components/result/HollandCodeSummary.vue'
import { getSessionById } from '../utils/sessionStorage'
import { HOLLAND_TYPES } from '../data/hollandTypes'
import { useToast } from 'vue-toastification'

export default {
  name: 'HollandResultPage',
  components: {
    BackToTop,
    HollandCodeDisplay,
    SixDimensionAnalysis,
    PrimaryTypeCard,
    CareerDirections,
    CareerAdvice,
    LearningPath,
    TypicalCareers,
    DeepTypeAnalysis,
    CareerPathPlanning,
    HollandFAQ,
    HollandCodeSummary
  },
  data() {
    return {
      sessionId: '',
      result: null,
      toast: null,
      // 三个类型的完整信息
      primaryTypeInfo: null,
      secondaryTypeInfo: null,
      tertiaryTypeInfo: null
    }
  },
  created() {
    this.toast = useToast()
    this.loadResult()
  },
  methods: {
    loadResult() {
      // 从路由获取sessionId
      this.sessionId = this.$route.params.id || this.$route.query.sessionId
      
      if (!this.sessionId) {
        this.toast.error('结果ID缺失')
        this.$router.push('/')
        return
      }

      // 从会话存储加载结果
      const session = getSessionById(this.sessionId)
      
      if (!session || !session.results) {
        this.toast.error('未找到测试结果')
        this.$router.push('/')
        return
      }

      this.result = session.results
      
      // 获取三个类型的完整信息
      if (this.result.hollandCode) {
        this.primaryTypeInfo = HOLLAND_TYPES[this.result.hollandCode[0]]
        this.secondaryTypeInfo = HOLLAND_TYPES[this.result.hollandCode[1]]
        this.tertiaryTypeInfo = HOLLAND_TYPES[this.result.hollandCode[2]]
      }
      
      console.log('Result loaded:', this.result)
      console.log('Primary type:', this.primaryTypeInfo)
    },
    saveResult() {
      // 这里可以实现保存到本地存储或分享功能
      this.toast.success('结果已保存到本地！')
    },
    retakeTest() {
      this.$router.push('/assessment/start')
    }
  }
}
</script>

<style scoped>
.prose p {
  margin-bottom: 1rem;
}
</style>

